import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { DataTable, Pagination } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { dt, title } from "@/lib/format";
import { SegmentBuilder } from "@/components/lists/SegmentBuilder";
import { ExportDialog } from "@/components/lists/ExportDialog";
import { useAuth } from "@/context/AuthContext";

export default function Segments() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [preview, setPreview] = useState(null);

  const combined = useQuery({
    queryKey: ["lists-and-segments", page],
    queryFn: () => get(`/lists-and-segments?page=${page}&page_size=25`),
    refetchInterval: 20000,
  });

  const save = useMutation({
    mutationFn: (payload) => (payload.id
      ? api.patch(`/segments/${payload.id}`, { name: payload.name, description: payload.description, filters: payload.filters })
      : api.post("/segments", payload)),
    onSuccess: () => {
      toast.success("Segment saved");
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ["lists-and-segments"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const action = useMutation({
    mutationFn: ({ id, verb }) => api.post(`/segments/${id}/${verb}`),
    onSuccess: (_, { verb }) => {
      toast.success(`Segment ${verb === "duplicate" ? "duplicated" : "archived"}`);
      queryClient.invalidateQueries({ queryKey: ["lists-and-segments"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const openPreview = async (row) => {
    try {
      const data = await get(`/segments/${row.id}/preview?limit=25`);
      setPreview({ ...row, ...data });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <>
      <PageHeader
        testId="segments-page"
        title="Lists & Smart Segments"
        subtitle="Physical lists hold imported contacts; smart segments are live filters that never copy data"
        actions={can("lists", "write") ? (
          <div className="flex gap-2">
            <Button variant="outline" asChild data-testid="go-to-lists">
              <Link to="/lists">Contact lists</Link>
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="new-segment-button"
              onClick={() => setEditing({ name: "", description: "", filters: { match: "and", conditions: [], groups: [], exclude: [] } })}>
              New smart segment
            </Button>
          </div>
        ) : null}
      />

      <Section testId="segments-section" title={`${combined.data?.total ?? 0} lists and segments`}>
        <DataTable
          testId="segments-table"
          rowTestId="segment-row"
          loading={combined.isLoading}
          columns={[
            { key: "name", label: "Name", render: (r) => (
              <span className="font-medium text-slate-800" data-testid={`segment-name-${r.id}`}>{r.name}</span>
            ) },
            { key: "type", label: "Type", render: (r) => (
              <StatusBadge value={r.type === "segment" ? "running" : "completed"}
                label={r.type === "segment" ? "Smart Segment" : "Physical List"}
                testId={`segment-type-${r.id}`} />
            ) },
            { key: "contacts", label: "Matching contacts", render: (r) => (r.contacts ?? 0).toLocaleString() },
            { key: "qualified_leads", label: "Qualified leads", render: (r) => (r.qualified_leads ?? 0).toLocaleString() },
            { key: "interested", label: "Interested", render: (r) => (r.interested ?? 0).toLocaleString() },
            { key: "hot", label: "Hot", render: (r) => (r.hot ?? 0).toLocaleString() },
            { key: "callback_requested", label: "Callbacks", render: (r) => (r.callback_requested ?? 0).toLocaleString() },
            { key: "converted", label: "Converted", render: (r) => (r.converted ?? 0).toLocaleString() },
            { key: "updated_at", label: "Last updated", render: (r) => dt(r.updated_at) },
            { key: "last_exported_at", label: "Last exported", render: (r) => (r.last_exported_at ? dt(r.last_exported_at) : "—") },
            { key: "created_by_name", label: "Created by", render: (r) => r.created_by_name || "—" },
            { key: "actions", label: "", render: (r) => (
              <div className="flex flex-wrap gap-1">
                {r.type === "list" ? (
                  <Button size="sm" variant="ghost" asChild data-testid={`open-list-${r.id}`}>
                    <Link to={`/lists/${r.id}`}>Open</Link>
                  </Button>
                ) : (
                  <Button size="sm" variant="ghost" onClick={() => openPreview(r)} data-testid={`preview-segment-${r.id}`}>Open</Button>
                )}
                {can("exports", "write") ? (
                  <Button size="sm" variant="ghost" onClick={() => setExporting(r)} data-testid={`export-${r.id}`}>Export</Button>
                ) : null}
                {r.type === "segment" && can("lists", "write") ? (
                  <>
                    <Button size="sm" variant="ghost" onClick={async () => {
                      const doc = await get(`/segments/${r.id}`);
                      setEditing({ id: doc.id, name: doc.name, description: doc.description, filters: doc.filters });
                    }} data-testid={`edit-segment-${r.id}`}>Edit filters</Button>
                    <Button size="sm" variant="ghost" onClick={() => action.mutate({ id: r.id, verb: "duplicate" })} data-testid={`duplicate-segment-${r.id}`}>Duplicate</Button>
                    <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => action.mutate({ id: r.id, verb: "archive" })} data-testid={`archive-segment-${r.id}`}>Archive</Button>
                  </>
                ) : null}
              </div>
            ) },
          ]}
          rows={combined.data?.items || []}
          empty={{ title: "Nothing here yet", description: "Import a contact list or create a smart segment." }}
        />
        <Pagination page={combined.data?.page || 1} pages={combined.data?.pages || 1} total={combined.data?.total || 0} onChange={setPage} />
      </Section>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-w-4xl bg-white" data-testid="segment-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit smart segment" : "New smart segment"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input data-testid="segment-name-input" value={editing?.name || ""} onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea rows={1} data-testid="segment-description-input" value={editing?.description || ""} onChange={(e) => setEditing((s) => ({ ...s, description: e.target.value }))} />
              </div>
            </div>
            {editing ? (
              <SegmentBuilder value={editing.filters} onChange={(filters) => setEditing((s) => ({ ...s, filters }))} />
            ) : null}
          </div>
          <DialogFooter>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={!editing?.name?.trim() || save.isPending}
              onClick={() => save.mutate(editing)} data-testid="segment-save">
              Save segment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(open) => !open && setPreview(null)}>
        <DialogContent className="max-w-3xl bg-white" data-testid="segment-preview-dialog">
          <DialogHeader><DialogTitle>{preview?.name} — {(preview?.count ?? 0).toLocaleString()} contacts</DialogTitle></DialogHeader>
          <div className="max-h-80 overflow-auto rounded-md border border-slate-200">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-2 py-1.5 text-left">Phone</th>
                  <th className="px-2 py-1.5 text-left">Name</th>
                  <th className="px-2 py-1.5 text-left">Outcome</th>
                  <th className="px-2 py-1.5 text-left">Interest</th>
                  <th className="px-2 py-1.5 text-left">Score</th>
                  <th className="px-2 py-1.5 text-left">Stage</th>
                </tr>
              </thead>
              <tbody>
                {(preview?.items || []).map((c) => (
                  <tr key={c.id} className="border-t border-slate-100" data-testid={`preview-row-${c.id}`}>
                    <td className="px-2 py-1.5 font-mono">{c.phone}</td>
                    <td className="px-2 py-1.5">{c.first_name} {c.last_name}</td>
                    <td className="px-2 py-1.5">{title(c.call_outcome || "—")}</td>
                    <td className="px-2 py-1.5">{title(c.interest_level || "unknown")}</td>
                    <td className="px-2 py-1.5">{c.interest_score ?? "—"}</td>
                    <td className="px-2 py-1.5">{title(c.pipeline_stage || "—")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setExporting(preview); setPreview(null); }} data-testid="preview-export">Export this segment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ExportDialog
        open={!!exporting}
        onClose={() => setExporting(null)}
        scopeType={exporting?.type === "segment" ? "segment" : "list"}
        scopeId={exporting?.id}
        scopeName={exporting?.name}
      />
    </>
  );
}
